import React, { useCallback, useRef, useState } from 'react';
import {
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  View,
  Text,
  TouchableOpacity,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { ActionButtons, FileUploadSection, FormInput, FormSection } from '@/components';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopCreateMessageView } from './desktop/DesktopCreateMessageView';
import { useCreateConversationMutation } from '@/store/api/admin/messagesApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { MessageTypeSelector } from '@/components/admin/messages/CreateMessage/components/MessageTypeSelector';
import { RecipientSelector } from '@/components/admin/messages/CreateMessage/components/RecipientSelector';
import { GroupChatConflictNotice } from '@/components/admin/messages/CreateMessage/components/GroupChatConflictNotice';
import { UnassignedRecipientsNotice } from '@/components/admin/messages/CreateMessage/components/UnassignedRecipientsNotice';
import { useCreateMessageForm } from '@/components/admin/messages/CreateMessage/hooks/useCreateMessageForm';
import { buildCreateConversationFormData } from '@/components/admin/messages/CreateMessage/utils/formData';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';

type CreateMessageScreenProps = StackScreenProps<AdminStackParamList, 'CreateMessage'>;

export const CreateMessageScreen: React.FC<CreateMessageScreenProps> = ({
  navigation,
}) => {
  const { redesign, admin } = useAdminColors();
  const { layoutMode } = useResponsive();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;

  const [createConversation, { isLoading }] = useCreateConversationMutation();
  const scrollViewRef = useRef<ScrollView>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Custom hook for form logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
    hasUnsavedChanges,
  } = useCreateMessageForm();

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!selectedPropertyId) {
      showApiError('No property selected');
      return;
    }

    // Check if form is valid before submission
    if (isLoading || !formData.subject || !formData.messageContent || formData.recipients.length === 0) {
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      showApiError('Please fix the form errors before submitting');
      return;
    }

    try {
      const formDataToSend = buildCreateConversationFormData(formData as any, selectedPropertyId);
      const result = await createConversation(formDataToSend).unwrap();

      if (result?.success) {
        showSuccess('Message created successfully');
        navigation.goBack();
      } else {
        showApiError('Failed to create message');
      }
    } catch (error: unknown) {
      console.error('Create message error:', error);
      const errorMessage = error && typeof error === 'object' && 'data' in error &&
        error.data && typeof error.data === 'object' && 'message' in error.data
        ? String(error.data.message)
        : 'Failed to create message';
      showApiError(errorMessage);
    }
  }, [
    selectedPropertyId,
    validateForm,
    formData,
    createConversation,
    showSuccess,
    showApiError,
    navigation,
  ]);

  // Handle back navigation with unsaved changes check
  const handleBack = useCallback(() => {
    if (hasUnsavedChanges()) {
      if (Platform.OS === 'web') {
        setShowCancelConfirm(true);
      } else {
        showDiscardAlert(() => navigation.goBack());
      }
    } else {
      navigation.goBack();
    }
  }, [hasUnsavedChanges, showDiscardAlert, navigation]);

  // Scroll to error section
  const scrollToError = useCallback(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ y: 0, animated: true });
    }
  }, []);

  const insets = useSafeAreaInsets();

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="CreateMessageDesktop">
        <DesktopCreateMessageView />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="CreateMessageScreen">
      <View style={[styles.container, { backgroundColor: redesign.bg.base }]}>
        {/* Custom Header */}
        <View
          style={[
            styles.header,
            {
              paddingTop: insets.top + 12,
              backgroundColor: redesign.bg.base,
              borderBottomColor: redesign.card.border,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleBack}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <SVG_COMPONENTS.CHEVRON_LEFT
              width={24}
              height={24}
              color={redesign.text.primary}
            />
          </TouchableOpacity>

          <Text style={[styles.headerTitle, { color: redesign.text.primary }]}>
            New Message
          </Text>

          <View style={styles.headerSpacer} />
        </View>

        {/* Main Content */}
        <KeyboardAvoidingView
          behavior="padding"
          style={styles.keyboardAvoidingView}
          keyboardVerticalOffset={-insets.bottom}
        >
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Subject Section */}
          <FormSection title="Message Details">
            <FormInput
              label="Subject"
              value={formData.subject}
              onChangeText={(value) => handleFieldChange('subject', value)}
              placeholder="Enter message subject"
              error={errors.subject}
              required
              maxLength={100}
            />

            <FormInput
              label="Message"
              value={formData.messageContent}
              onChangeText={(value) => handleFieldChange('messageContent', value)}
              placeholder="Enter your message"
              error={errors.messageContent}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              required
              maxLength={1000}
            />
          </FormSection>

          {/* Recipients Section */}
          <RecipientSelector
            recipients={formData.recipients}
            onRecipientsChange={(recipients) => handleFieldChange('recipients', recipients)}
            error={errors.recipients}
            messageType={formData.messageType}
          />

          {/* Message Type Section */}
          <MessageTypeSelector
            messageType={formData.messageType}
            onMessageTypeChange={(type) => handleFieldChange('messageType', type)}
            recipientCount={formData.recipients.length}
          />

          {/* Group chat conflict (guarantors with mixed leases) */}
          <GroupChatConflictNotice mismatchedGuarantors={formData.mismatchedGuarantors as any} />

          {/* Unassigned recipients (invitation flow) */}
          <UnassignedRecipientsNotice recipients={formData.recipients as any} />

          {/* Files Section */}
          <FileUploadSection
            files={formData.files}
            onFilesChange={(files) => handleFieldChange('files', files)}
            maxFiles={5}
            title="Attachments"
            subtitle="Add files to your message (optional)"
          />

          {/* Email Notification Toggle */}
          <FormSection title="Notification Settings">
            <TouchableOpacity
              onPress={() => handleFieldChange('sendEmail', !formData.sendEmail)}
              activeOpacity={0.8}
            >
              <GlassCard padding={16} style={styles.toggleCard}>
                <View style={styles.toggleRow}>
                  <View style={styles.toggleContent}>
                    <Text style={[styles.toggleLabel, { color: redesign.text.primary }]}>
                      Send email notification
                    </Text>
                    <Text style={[styles.toggleSubtext, { color: redesign.text.muted }]}>
                      Recipients will receive an email notification about this message
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.toggle,
                      { borderColor: redesign.card.border },
                      formData.sendEmail && {
                        backgroundColor: admin.formInput.borderFocused,
                        borderColor: admin.formInput.borderFocused,
                      },
                    ]}
                  >
                    {formData.sendEmail && (
                      <SVG_COMPONENTS.CHECK_CIRCLE
                        width={16}
                        height={16}
                        color="#FFFFFF"
                      />
                    )}
                  </View>
                </View>
              </GlassCard>
            </TouchableOpacity>
          </FormSection>
        </ScrollView>

        {/* Action Buttons */}
        <ActionButtons
          isLoading={isLoading}
          onCancel={handleBack}
          onSubmit={handleSubmit}
          submitText="Send Message"
          cancelText="Cancel"
          containerStyle={styles.actionButtonsFlow}
        />
        </KeyboardAvoidingView>
        {Platform.OS === 'web' && (
          <WebConfirmModal
            visible={showCancelConfirm}
            title={'Discard Changes'}
            message={'You have unsaved changes. Are you sure you want to discard them?'}
            confirmText={'Discard'}
            cancelText={'Keep Editing'}
            onConfirm={() => {
              setShowCancelConfirm(false);
              navigation.goBack();
            }}
            onCancel={() => setShowCancelConfirm(false)}
          />
        )}
      </View>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 16,
  },
  toggleCard: {
    marginTop: 8,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  toggleContent: {
    flex: 1,
    marginRight: 16,
  },
  toggleLabel: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 4,
  },
  toggleSubtext: {
    fontSize: 14,
    lineHeight: 18,
  },
  toggle: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionButtonsFlow: {
    position: 'relative' as const,
  },
});
